'use client';
import { useState, useEffect } from 'react';
import { getVideos } from '@/sanity/queries';
import { Col, Row } from 'react-bootstrap';
import VideoGrid from '../videoGrid/videoGrid';
import { urlForImage } from '@/sanity/lib/utils';
import { GetVideosResult } from '@/sanity.types';
import './index.scss';
import JsonLd from '@/components/JsonLd';
import { imageData } from '@/config/defaultBlur';

interface VideoContentProps {
  slug: string | null;
  excludeIds: string[];
}

interface ImageAsset {
  _ref: string;
  _type: 'reference';
}

interface FullImageAsset {
  _id: string;
  _type: 'sanity.imageAsset';
  metadata?: {
    lqip?: string;
  };
}

interface Thumbnail {
  alt?: string;
  asset: ImageAsset;
  _type: 'image';
  imgAsset?: FullImageAsset;
}

interface GroupedVideos {
  [category: string]: {
    title: string;
    slug: string;
    thumbnail: Thumbnail;
  }[];
}

// Function to group videos by category
function groupVideosByCategory(videos: GetVideosResult): GroupedVideos {
  return videos.reduce((grouped: GroupedVideos, video) => {
    // Skip videos that don't have a category
    if (!video.category?.title) {
      return grouped;
    }

    const category = video.category.title;

    if (!grouped[category]) {
      grouped[category] = [];
    }

    grouped[category].push({
      title: video.title,
      slug: video.slug.current,
      thumbnail:
        video.thumbnail && video.thumbnail.asset
          ? {
              alt: video.thumbnail.alt || '',
              asset: video.thumbnail.asset,
              _type: 'image',
              imgAsset: video.thumbnail.imgAsset || undefined,
            }
          : {
              asset: { _ref: '', _type: 'reference' },
              _type: 'image',
            },
    });

    return grouped;
  }, {});
}

const VideoPosts = ({ slug, excludeIds }: VideoContentProps) => {
  const [activeTab, setActiveTab] = useState<string>('All');
  const [categoryWiseVideo, setCategoryWiseVideo] = useState<GroupedVideos>({});

  useEffect(() => {
    const fetchVideos = async () => {
      const response = await fetch('/api/videopage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: getVideos,
          params: {
            excludeIds: excludeIds || [],
          },
        }),
      });

      const videoResult = await response.json();

      if (!slug) {
        const groupedVideos = groupVideosByCategory(videoResult);
        setCategoryWiseVideo(groupedVideos);
      }
    };

    fetchVideos();
  }, [slug, excludeIds]);
  const focusElement = (element: Element | null) => {
    if (element instanceof HTMLElement) {
      element.focus();
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent,
    tabTitle: string,
    tabsArray: { title: string }[]
  ) => {
    const currentIndex = tabsArray.findIndex((tab) => tab.title === tabTitle);

    switch (event.key) {
      case 'Tab':
        if (!event.shiftKey && currentIndex < tabsArray.length - 1) {
          event.preventDefault();

          focusElement((event.target as HTMLElement).nextElementSibling);
        } else if (event.shiftKey && currentIndex > 0) {
          event.preventDefault();

          focusElement((event.target as HTMLElement).previousElementSibling);
        }
        break;
      case 'Enter':
      case 'Space':
        event.preventDefault();
        setActiveTab(tabTitle);
        break;
    }
  };
  const tabs = [
    { title: 'All' },
    ...Object.keys(categoryWiseVideo).map((category) => ({
      title: category,
    })),
  ];

  const videoListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    about: {
      '@type': 'ItemList',
      name: 'Video Categories',
      itemListElement: tabs.map((item, index) => ({
        '@type': 'ListItem',
        position: `Category-${index + 1}`,
        name: item?.title,
      })),
    },
    itemListElement: Object.entries(categoryWiseVideo).flatMap(
      ([category, videos]) =>
        videos.map((video, index) => ({
          '@type': 'VideoObject',
          position: index + 1,
          name: video?.title,
          thumbnailUrl: urlForImage(video?.thumbnail)?.url(),
          url: `${process.env.NEXT_PUBLIC_SITE_URL}/videos/${video?.slug}`,
          genre: category,
        }))
    ),
  };

  return (
    <>
      <JsonLd data={videoListJsonLd} />
      <ul
        className="custom-tab"
        role="tablist"
        aria-label="Tabs for categories"
      >
        {tabs.map((tab, index) => (
          <li
            key={index}
            role="tab"
            tabIndex={0}
            className={`tab-link ${activeTab === tab.title ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.title)}
            id={`tab-${tab.title}`}
            aria-selected={activeTab === tab.title}
            aria-controls={`panel-${tab.title}`}
            onKeyDown={(e) => handleKeyDown(e, tab.title, tabs)}
          >
            {tab.title}
          </li>
        ))}
      </ul>

      <div
        aria-live="polite"
        aria-labelledby={`tab-content ${activeTab}`}
        role="tabpanel"
      >
        {activeTab === 'All' ? (
          <Row>
            {Object.entries(categoryWiseVideo).map(
              ([category, videos], index) => (
                <div key={index}>
                  <h2 className="section-title">{category}</h2>
                  <Row>
                    {videos.map((video) => (
                      <Col
                        xs={12}
                        sm={6}
                        md={4}
                        className="pb-5"
                        key={`${video?.slug}-${index}`}
                        aria-labelledby={video?.title}
                      >
                        <VideoGrid
                          videoTitle={video?.title}
                          src={
                            (urlForImage(video?.thumbnail)?.url() as string) ||
                            '/n_images/default-blog.jpg'
                          }
                          href={`/videos/${video?.slug}`}
                          blurDataURL={
                            video?.thumbnail?.imgAsset?.metadata?.lqip ||
                            imageData.blurImage
                          }
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              )
            )}
          </Row>
        ) : (
          <Row>
            {(categoryWiseVideo[activeTab] || []).map((video, index) => (
              <Col
                xs={12}
                md={4}
                className="pb-5"
                key={`${video?.slug}-${index}`}
                aria-labelledby={video?.title}
              >
                <VideoGrid
                  videoTitle={video?.title}
                  src={
                    (urlForImage(video.thumbnail)?.url() as string) ||
                    '/n_images/default-blog.jpg'
                  }
                  href={`/videos/${video?.slug}`}
                  blurDataURL={
                    video?.thumbnail?.imgAsset?.metadata?.lqip ||
                    imageData.blurImage
                  }
                />
              </Col>
            ))}
          </Row>
        )}
      </div>
    </>
  );
};

export default VideoPosts;
